// /src/pages/AudioLanguage/AddLanguages.js

import React, { useState, useEffect, useRef } from "react"; // Added useRef
import axios from "axios";
import Select from "react-select";
import backendURL from "../../config";

// Assuming Bootstrap 5+ for form-switch
// Make sure Bootstrap CSS is properly included in your project

function AddLanguages({ chatbotId, onClose, setReload }) {
  const [documents, setDocuments] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null); // Keep null initially
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);
  const audioPlayerRef = useRef(null); // Use useRef for audio element
  const [isDefaultLanguage, setIsDefaultLanguage] = useState(false); // Use boolean for switch state
  const [feedback, setFeedback] = useState({ message: null, type: null }); // For success/error messages
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  
  useEffect(() => {
    // Create or update audio element when src changes
    if (audioSrc) {
      if (!audioPlayerRef.current) {
        audioPlayerRef.current = new Audio(audioSrc);
      } else {
        audioPlayerRef.current.src = audioSrc;
      }
    } else {
       // Optional: clear audio element if src is nullified
       if (audioPlayerRef.current) {
           audioPlayerRef.current.pause();
           audioPlayerRef.current.removeAttribute('src'); // Prevent holding onto old source
           // audioPlayerRef.current = null; // Or fully remove it
       }
    }
  }, [audioSrc]);


  // Fetch documents on component mount
  const fetchDocuments = async (currentChatbotId) => {
    setIsLoading(true);
    setFeedback({ message: null, type: null });
    try {
      const token = localStorage.getItem("access_token");
       if (!token) {
         setFeedback({ message: "Authentication token not found. Please log in.", type: "error" });
         setIsLoading(false);
         return;
       }

      const params = currentChatbotId ? { chatbot_id: currentChatbotId } : {};

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${backendURL}/get-languages`, { headers, params });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

      // Add a check if response.data is actually an array
      if (Array.isArray(responseData)) {
           setDocuments(responseData);
           console.log("Get lang data: ", responseData);
      } else {
          console.error("Received non-array data from /get-languages:", responseData);
          setDocuments([]); // Set to empty array to avoid map errors
          setFeedback({ message: "Received invalid language data format from server.", type: "error" });
      }
    } catch (error) {
      handleError(error, "fetching languages");
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Audio & Language Settings | KlonIT Web Application";
    console.log(chatbotId)
    if (chatbotId) {
        fetchDocuments(chatbotId);
    }

    // Cleanup function to pause audio if component unmounts while playing
    return () => {
      audioPlayerRef.current?.pause();
    };
  }, [chatbotId]); // Empty dependency array ensures this runs only once on mount


  // Handle API call errors
  const handleError = (error, action = "performing action") => {
    let message = `Error ${action}. Please try again.`;
    if (error.response) {
      // Server responded with a status code outside 2xx range
       message = error.response.data.message || error.response.data.message || `Server Error: ${error.response.status}`;
       console.error(`Error Response Data (${action}):`, error.response.data);
       console.error(`Error Response Status (${action}):`, error.response.status);
    } else if (error.request) {
      // Request was made but no response received
      message = "No response from server. Check network connection or backend status.";
      console.error(`Error Request (${action}):`, error.request);
    } else {
      // Something happened in setting up the request
      message = error.message;
      console.error(`Error Message (${action}):`, error.message);
    }
     setFeedback({ message, type: "error" });
  };

  // --- Options Preparation ---
  const languageOptions = React.useMemo(() => {
      if (!Array.isArray(documents)) return []; // Guard against non-array
      // Use a Set to get unique country values, then map to react-select format
      return [...new Set(documents.map((doc) => doc.country))]
        .sort() // Optional: sort languages alphabetically
        .map((country) => ({
          value: country,
          label: country,
        }));
  }, [documents]);


  const voiceOptions = React.useMemo(() => {
     if (!Array.isArray(documents) || !selectedLanguage || !selectedGender) return []; // Guard and require selections
     // Filter based on current language and gender selections
     return documents
        .filter(
            (doc) =>
            doc.country === selectedLanguage.value &&
            doc.gender === selectedGender
        )
        .map((doc) => ({
            value: doc.voice_name, // The unique identifier for the voice
            label: doc.name,       // The display name for the voice
            // Store other relevant data if needed upon selection
            lang_code: doc.lang_code,
            lang_flag: doc.lang_flag,
            website_lang_code: doc.language,
        }));
  }, [documents, selectedLanguage, selectedGender]);


  // --- Handlers for Selection Changes ---
  const handleLanguageChange = (selectedOption) => {
    setSelectedLanguage(selectedOption);
    setSelectedVoice(null); // Reset voice when language changes
    setAudioSrc(null); // Reset audio source
    setFeedback({ message: null, type: null }); // Clear feedback
  };

  const handleGenderChange = (event) => {
    setSelectedGender(event.target.value); // Directly set from radio value
    setSelectedVoice(null); // Reset voice when gender changes
    setAudioSrc(null); // Reset audio source
    setFeedback({ message: null, type: null }); // Clear feedback
  };


  const handleVoiceChange = (selectedOption) => {
    setSelectedVoice(selectedOption);
    setFeedback({ message: null, type: null }); // Clear feedback
    if (selectedOption) {
      try {
        // Assuming voice_name corresponds to the audio file name
        const audioFileName = `${selectedOption.value}.wav`;
        // IMPORTANT: Using require like this might only work well if all audio files
        // are known at build time. If files are added dynamically or come from
        // a user upload / external source, you'll need a different approach
        // (e.g., serving them from a public folder or backend endpoint).
        const audioPath = require(`../../assets/audio/${audioFileName}`);
        setAudioSrc(audioPath);
      } catch (err) {
          console.error("Error loading audio file:", err);
          setAudioSrc(null); // Clear src on error
          setFeedback({ message: `Audio preview for ${selectedOption.label} not found.`, type: "warning" });
      }
    } else {
      setAudioSrc(null); // Clear audio source if voice is deselected
    }
  };

  const handleDefaultLanguageChange = (event) => {
    setIsDefaultLanguage(event.target.checked);
    setFeedback({ message: null, type: null }); // Clear feedback
  };

  // --- Audio Playback Controls ---
  const handlePlayButtonClick = () => {
    if (audioPlayerRef.current && audioPlayerRef.current.src) {
        audioPlayerRef.current.play().catch(err => {
             console.error("Audio play failed:", err);
             setFeedback({ message: "Could not play audio preview.", type: "error" });
        });
    } else {
        setFeedback({ message: "Select a voice to preview.", type: "warning" });
    }
  };

  // Removed handleStopButtonClick - standard controls usually sufficient, or add if needed

  // --- Handle API Call to Save Settings ---
  const handleApiCall = async () => {
     // Basic Validation
    if (!selectedLanguage || !selectedGender || !selectedVoice) {
      setFeedback({ message: "Please select a language, gender, and voice.", type: "error" });
      return;
    }

    // Find the selected document details directly from the selectedVoice option
    // This is more efficient than searching the whole documents array again
    const selectedVoiceData = voiceOptions.find(opt => opt.value === selectedVoice.value);

     if (!selectedVoiceData) {
        setFeedback({ message: "Internal error: Could not find selected voice data.", type: "error" });
        console.error("Mismatch between selectedVoice and voiceOptions", selectedVoice, voiceOptions);
        return;
    }


    setIsSaving(true);
    setFeedback({ message: null, type: null });

    const data = {
      language: selectedLanguage.value,         // e.g., "English (United States)"
      website_lang_code: selectedVoiceData.website_lang_code, // e.g., "en-US"
      voice: selectedVoice.value,               // e.g., "en-US-Standard-A"
      gender: selectedGender,                   // e.g., "male" or "female"
      default_lang: isDefaultLanguage ? 1 : 0,  // Send 1 for true, 0 for false
      lang_code: selectedVoiceData.lang_code,   // e.g., "en"
      lang_flag: selectedVoiceData.lang_flag,   // e.g., "🇺🇸"
      chatbot_id: chatbotId
    };

    try {
      const token = localStorage.getItem('access_token');
        if (!token) {
         setFeedback({ message: "Authentication token not found. Please log in.", type: "error" });
         setIsSaving(false);
         return;
       }
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(`${backendURL}/add-language-audio`, data, { headers });
      setFeedback({ message: response.data.message || "Language added successfully!", type: "success" });

      // Close the drawer and reload only on successful add (e.g., status 201 Created)
      if (response.status === 201 || response.status === 200) { // Check for 200 OK as well
        // Delay closing slightly to allow user to see success message
        setTimeout(() => {
            onClose(); // Call the function passed from parent to close the modal/drawer
            setReload(); // Call the function passed from parent to trigger data reload
        }, 1500); // 1.5 second delay
      }

    } catch (error) {
      handleError(error, "adding language");
    } finally {
        setIsSaving(false);
    }
  };

  // --- Custom Styles for React Select ---
  const customSelectStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999, // Keep z-index if needed for modals/drawers
    }),
     // Optional: Add styles for control height, etc., if needed
     // control: (provided) => ({ ...provided, minHeight: '38px', height: '38px' }),
     // valueContainer: (provided) => ({ ...provided, height: '38px', padding: '0 6px' }),
     // input: (provided) => ({ ...provided, margin: '0px' }),
     // indicatorSeparator: () => ({ display: 'none' }),
     // indicatorsContainer: (provided) => ({ ...provided, height: '38px' }),
  };


  // --- Render Logic ---
  if (isLoading) {
      return <div className="p-3">Loading language options...</div>;
  }

  return (
    // Using p-3 or p-4 for padding inside the modal/drawer content area
    <div className="p-3">
       {/* Feedback Alert */}
       <h3 className="text-center mb-5 mt-5">Add Langauge Audio</h3>
       {feedback.message && (
        <div className={`alert ${feedback.type === 'error' ? 'alert-danger' : feedback.type === 'success' ? 'alert-success' : 'alert-warning'} alert-dismissible fade show`} role="alert">
          {feedback.message}
          <button type="button" className="btn-close" onClick={() => setFeedback({ message: null, type: null })} aria-label="Close"></button>
        </div>
      )}

      {/* Language Selection */}
      <div className="mb-5">
        <label htmlFor="LanguageSelectionSelect" className="form-label fw-bold">Select Website Language <span className="text-danger">*</span></label>
        <Select
          id="LanguageSelectionSelect"
          inputId="LanguageSelectionSelectInput" // Better for accessibility linking
          options={languageOptions}
          onChange={handleLanguageChange}
          value={selectedLanguage}
          styles={customSelectStyles}
          placeholder="Choose a language..."
          isDisabled={isSaving}
        />
      </div>

      {/* Gender Selection (Radio Buttons) */}
      {selectedLanguage && ( // Only show gender if language is selected
        <div className="mb-5">
            <label className="form-label fw-bold d-block">Select Gender <span className="text-danger">*</span></label> {/* d-block for spacing */}
            <div className="form-check form-check-inline">
                <input
                    className="form-check-input"
                    type="radio"
                    name="genderOptions"
                    id="genderMale"
                    value="male"
                    checked={selectedGender === "male"}
                    onChange={handleGenderChange}
                    disabled={isSaving}
                 />
                 <label className="form-check-label" htmlFor="genderMale">
                    Male
                 </label>
            </div>
            <div className="form-check form-check-inline">
                <input
                    className="form-check-input"
                    type="radio"
                    name="genderOptions"
                    id="genderFemale"
                    value="female"
                    checked={selectedGender === "female"}
                    onChange={handleGenderChange}
                    disabled={isSaving}
                />
                <label className="form-check-label" htmlFor="genderFemale">
                    Female
                </label>
            </div>
        </div>
      )}


      {/* Voice Selection */}
      {selectedLanguage && selectedGender && ( // Only show voice if lang and gender selected
        <div className="mb-5">
            <label htmlFor="VoiceSelectionSelect" className="form-label fw-bold">Select Voice <span className="text-danger">*</span></label>
            <div className="d-flex align-items-center"> {/* Flexbox for inline layout */}
                <div className="flex-grow-1"> {/* Select takes available space */}
                    <Select
                        id="VoiceSelectionSelect"
                        inputId="VoiceSelectionSelectInput"
                        options={voiceOptions}
                        onChange={handleVoiceChange}
                        value={selectedVoice}
                        styles={customSelectStyles}
                        placeholder="Choose a voice..."
                        isDisabled={!selectedLanguage || !selectedGender || isSaving}
                        noOptionsMessage={() => voiceOptions.length === 0 && documents.length > 0 ? 'No voices match selection' : 'Select language and gender first'}
                    />
                </div>
                <button
                    type="button"
                    className="btn btn-secondary ms-2 flex-shrink-0" // Margin start, prevent shrinking
                    onClick={handlePlayButtonClick}
                    disabled={!selectedVoice || isSaving}
                    aria-label="Play selected voice preview"
                >
                    <i className="fa fa-play"></i> {/* Ensure Font Awesome is included */}
                </button>
            </div>
        </div>
       )}

       {/* Default Language Switch */}
       <div className="mb-5 mt-5"> {/* More margin top/bottom */}
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="defaultLangSwitch"
              checked={isDefaultLanguage}
              onChange={handleDefaultLanguageChange}
              disabled={isSaving}
            />
            <label className="form-check-label fw-bold" htmlFor="defaultLangSwitch">
              Set as Default Language for Website
            </label>
          </div>
       </div>

       {/* Action Button */}
       <button
         type="button"
         className="btn btn-primary w-100" // Make button full width for modal context
         onClick={handleApiCall}
         disabled={!selectedLanguage || !selectedGender || !selectedVoice || isSaving || isLoading}
       >
         {isSaving ? (
            <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                 Saving...
            </>
         ) : (
            "Add New Language"
         )}
       </button>

    </div> // End of padding container
  );
}

export default AddLanguages;